import React, { useState, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import webChatHistoryService, { EnhancedChatSession } from '../services/webChatHistoryService';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ShareIcon,
  UserGroupIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { BookMarked, PanelLeft } from 'lucide-react';
import UserMenu from './UserMenu';
import SidebarBrand from './SidebarBrand';
import Glocke from './Glocke';
import { zoomVon } from './Erklaerung';
import SharedResearchPanel, { MomentaufnahmeTeilenDialog } from './SharedResearchPanel';
import SidebarRunIndicator, { sidebarRunState } from './SidebarRunIndicator';
import { useRISearch } from '../contexts/RISearchContext';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { INHALTE_GESPERRT } from '../config/ebenen';
import { researchLock } from '../pages/accessSummary';
import type { PersonalAccess } from '../administration/api';
import { lifecyclePhaseText } from '../administration/lifecycleText';
import { EinstLink } from '../pages/einstellungen/EinstLink';
import { naechstesZiel } from '../pages/einstellungen/fokus';
import { einstellungsZiel, persoenlicheAdresse, seitenTitel } from '../pages/einstellungen/seiten';
import { Dialog } from '../administration/components';
import { listenZeit, zeitgruppe, type Zeitgruppe } from '../utils/datum';
import { VORLAGEN_TEXTE } from '../pages/vorlagen/vorlagenTexte';

interface AppSidebarProps {
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  refreshTrigger?: number;
  // Nachtrag A2: Die Hülle setzt den Fokus nach Escape auf den Knopf „Seitenleiste erweitern“ (bis F-23 „Sidebar erweitern“).
  expandButtonRef?: React.RefObject<HTMLButtonElement>;
}

type GroupedSessions = Record<Zeitgruppe, EnhancedChatSession[]>;

// Zustände, in denen der Server /api/ris ablehnt; Grenzen und Kosten sperren nur neue Recherchen, nicht die Liste.
const SUCHLISTE_GESPERRT: PersonalAccess['status'][] = ['ORGANIZATION_BLOCKED', 'PAYMENT_OVERDUE', 'ACCESS_BLOCKED', 'NO_SEAT', 'EXPIRED', 'PLATFORM_BLOCKED'];

interface VerlaufMenueProps {
  titel: string;
  // „Momentaufnahme teilen“ gibt es nur in einer Kunden- oder der internen Organisation (teilenIn der Leiste).
  teilen: boolean;
  onUmbenennen: (event: React.MouseEvent) => void;
  onTeilen: () => void;
  onLoeschen: (event: React.MouseEvent) => void;
  // DR-13 UI-19: solange ein Dialog aus diesem Menü offen ist, bleibt „…“ sichtbar; beim Schließen kehrt der Fokus dorthin zurück.
  halten?: boolean;
}

// Einträge des Menüs: 44 px hoch, 14 px Schrift; „Löschen“ behält die rote Hervorhebung des früheren Symbolknopfs.
const menueEintragForm = 'flex w-full min-h-11 items-center gap-2 rounded px-3 text-left text-sm text-primary dark:text-white';
const menueEintrag = `${menueEintragForm} hover:bg-warm-100 dark:hover:bg-gray-600`;
const menueEintragLoeschen = `${menueEintragForm} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30`;

// F10-27 (Korrekturlauf 09.10.2026): die Aktionen eines Verlaufs hinter einem Knopf „…“ mit kleinem Menü, damit sie auch
// auf Telefon und Tablet erreichbar sind (vorher drei Symbolknöpfe nur beim Überfahren oder bei Tastaturfokus).
// Sichtbar: unter 768 px und auf Touchgeräten immer (44 px), sonst beim Überfahren, bei Tastaturfokus und solange das Menü
// offen ist (32 px mit 44 px Trefferfläche). Das Menü steht im DOM beim Knopf und schwebt fixiert an ihm, damit die
// scrollende Liste es nicht abschneidet (Lage wie Zeilenmenue in pages/einstellungen/Bausteine.tsx: die Leiste bindet
// fixierte Elemente an sich, deshalb erst bei 0/0 messen). Escape, Klick außerhalb, Scrollen und Verlassen mit der
// Tastatur schließen; nach Escape liegt der Fokus wieder auf „…“.
function VerlaufMenue({ titel, teilen, onUmbenennen, onTeilen, onLoeschen, halten = false }: VerlaufMenueProps) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const listeId = useId();
  const name = `Aktionen für „${titel}“`;
  const schliessen = (zurueck: boolean) => { setOffen(false); if (zurueck) knopf.current?.focus({ preventScroll: true }); };

  useLayoutEffect(() => {
    const k = knopf.current, l = liste.current;
    if (!offen || !k || !l) return;
    l.style.left = '0px'; l.style.top = '0px';
    const basis = l.getBoundingClientRect(), r = k.getBoundingClientRect();
    const z = zoomVon(l), hoehe = l.offsetHeight * z, breite = l.offsetWidth * z;
    const top = r.bottom + 4 + hoehe <= window.innerHeight - 8 ? r.bottom + 4 : Math.max(8, r.top - 4 - hoehe);
    const left = Math.min(Math.max(8, r.right - breite), Math.max(8, window.innerWidth - breite - 8));
    l.style.left = `${Math.round((left - basis.left) / z)}px`; l.style.top = `${Math.round((top - basis.top) / z)}px`;
    l.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const aussen = (event: MouseEvent) => {
      const ziel = event.target as Node;
      if (!liste.current?.contains(ziel) && !knopf.current?.contains(ziel)) setOffen(false);
    };
    const zu = () => setOffen(false);
    document.addEventListener('mousedown', aussen, true);
    window.addEventListener('scroll', zu, true);
    window.addEventListener('resize', zu);
    return () => {
      document.removeEventListener('mousedown', aussen, true);
      window.removeEventListener('scroll', zu, true);
      window.removeEventListener('resize', zu);
    };
  }, [offen]);

  // Escape schließt nur das Menü (die Hülle klappt sonst mobil die Leiste ein); Pfeiltasten gehen durch die Einträge.
  const tasten = (event: React.KeyboardEvent) => {
    if (!offen) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); schliessen(true); return; }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const eintraege = Array.from(liste.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const ziel = naechstesZiel(eintraege.length, eintraege.indexOf(document.activeElement as HTMLElement), event.key === 'ArrowUp');
    if (ziel < 0) return;
    event.preventDefault();
    eintraege[ziel].focus();
  };

  return (
    <div
      className="absolute right-1 top-1"
      onKeyDown={tasten}
      // Verlässt der Fokus das Menü zu einem anderen Element (Tab), schließt es. Ohne neues Ziel nicht: Safari auf Mac,
      // iPhone und iPad gibt einem angeklickten Knopf keinen Fokus, das Menü verschwände sonst vor dem Klick auf den
      // Eintrag; den Klick daneben fängt der Zuhörer oben ab.
      onBlur={event => { if (offen && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOffen(false); }}
    >
      <button
        ref={knopf}
        type="button"
        aria-label={name}
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-controls={offen ? listeId : undefined}
        onClick={() => setOffen(wert => !wert)}
        className={`relative flex h-11 w-11 items-center justify-center rounded-lg text-muted hover:text-primary hover:bg-warm-100 dark:hover:bg-gray-600 md:h-8 md:w-8 md:before:absolute md:before:-inset-1.5 md:before:content-[''] [@media(pointer:coarse)]:!flex [@media(pointer:coarse)]:!h-11 [@media(pointer:coarse)]:!w-11 ${offen || halten ? '' : 'md:hidden md:group-hover:flex md:group-focus-within:flex'}`}
      >
        <EllipsisHorizontalIcon className="w-5 h-5" aria-hidden="true" />
      </button>
      {offen && (
        <div
          ref={liste}
          id={listeId}
          role="menu"
          aria-label={name}
          className="fixed z-30 min-w-[220px] rounded-md border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 bg-warm-card p-1"
        >
          <button type="button" role="menuitem" className={menueEintrag} onClick={event => { setOffen(false); onUmbenennen(event); }}>
            <PencilIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>Umbenennen</span>
          </button>
          {teilen && (
            <button type="button" role="menuitem" className={menueEintrag} onClick={() => { schliessen(true); onTeilen(); }}>
              <ShareIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>Momentaufnahme teilen</span>
            </button>
          )}
          <button type="button" role="menuitem" className={menueEintragLoeschen} onClick={event => { schliessen(true); onLoeschen(event); }}>
            <TrashIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>Löschen</span>
          </button>
        </div>
      )}
    </div>
  );
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  currentSessionId,
  onSessionSelect,
  onNewChat,
  isCollapsed,
  onToggleCollapse,
  refreshTrigger,
  expandButtonRef,
}) => {
  const { isSessionSearching, isDetailRunning, endedRunCount, resumeActiveRuns, seenRunIds } = useRISearch();
  // AE-11: in einer laufenden Sicht sind die Inhalte der Personen gesperrt (Positivliste); die Leiste fragt keine
  // Recherchen an und sagt das, statt mit einem Ladefehler zu enden.
  const { user } = useAuth();
  const gesperrt = !!user?.sicht?.aktiv;
  // Plan 1a E24 (Funktionsprobe fd F6): ein Konto ohne Recherche über die Plattform (access.status PLATFORM_BLOCKED, etwa
  // der Betrieb) hat keine Suchliste; die Leiste fragt sie nicht an (sonst 403 und Konsolenfehler) und zeigt den Leersatz.
  // VE-02 (b): die Bedingung läuft nur über den Zugang des Servers, nicht über Plattformbits.
  // Knopf-Prüfung (Schlusslauf 2): ebenso ohne Recherchezugang, also in genau den Zuständen, in denen der Server die
  // Suchliste mit 403 ablehnt (backend/src/middleware/auth.ts: gesperrt, pausiert, ohne Platz, abgelaufen, Plattformkonto).
  const zugangOhneSuchliste = !!user?.access && SUCHLISTE_GESPERRT.includes(user.access.status);
  const ohneSuchliste = gesperrt || zugangOhneSuchliste;
  // Paket 12, RSM-25: ohne Recherchezugang ist „Neue Recherche“ gesperrt und nennt den Grund wie die Statuszeile in
  // „Mein Zugang“ (Lebenszyklusphase wie AccountAccess.tsx). Der Link öffnet „Mein Zugang“ im Einstellungen-Pop-up (AE-1);
  // das Ziel kommt vom Server (VE-02). In einer Sicht sagt die Liste darunter schon, dass Inhalte gesperrt sind.
  // W2-A2: nach einem Austritt „Ohne {Begriff}“ wie in „Mein Zugang“; den Begriff liefert die Marke (wie ResearchAccess.tsx).
  // Plan 1a Welle 2 (UP-03): die Kontofelder (user.account) als vierter Parameter, damit die Sperre ohne Lizenzplatz dasselbe
  // sagt wie die Karte daneben („Ohne Lizenzplatz keine Recherche.“ in den Fällen B und C).
  const brand = useBrand();
  const organisation = user?.access?.organization;
  const sperre = researchLock(user?.access, lifecyclePhaseText(organisation?.lifecycle?.projected ? null : organisation?.lifecycle, organisation?.kind || 'MANDANT', organisation?.status), brand.tenantLabel, user?.account);
  const sperrHinweis = sperre.locked && !(gesperrt && sperre.reason === INHALTE_GESPERRT);
  // Erste Zahlung nur mit canPay, auch bei offenem Tarifauftrag; nie in einer Sicht.
  const tarifwahl = organisation?.status === 'PENDING_PAYMENT' && user?.account?.canPay && !gesperrt;
  const zugangZiel = tarifwahl ? '/tarif-waehlen' : einstellungsZiel(user?.ebenen?.meineEinstellungen?.ziel || '/zugang', user?.ebenen) || persoenlicheAdresse('zugang');
  // Paket 10 B10 (KAN-26, KAN-30): Momentaufnahmen nur für Mitglieder einer Kunden- oder der internen Organisation; in einer
  // Sicht kein bedienbarer Einstieg (VE-25), dort zeigt die Leiste ohnehin den Sperrsatz. Der Wert ist der Name für die Sätze
  // der Dialoge (MK-1c, F10-11).
  const zugang = user?.access;
  const teilenIn = !gesperrt && !zugang?.sichtGesperrt && organisation && (organisation.kind === 'MANDANT' || organisation.kind === 'INTERNAL')
    ? organisation.name
    : null;
  const [geteiltOffen, setGeteiltOffen] = useState(false);
  const [teilen, setTeilen] = useState<EnhancedChatSession | null>(null);
  // DR-13 UI-19: die Rückfrage vor dem Löschen eines Verlaufs als Dialog der Anwendung statt confirm() des Browsers.
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null);
  // Profil-Nachbau F-20.3 (BAU-VORLAGEN.md 5.3): „Vorlagen“ führt zur App-Seite /vorlagen, auch bei gesperrter Recherche
  // (Vorlagen pflegen geht immer); auf der Seite ist der Eintrag als aktuelle Seite markiert.
  const navigate = useNavigate();
  const aufVorlagen = useLocation().pathname === '/vorlagen';

  // Sessions state
  const [risSessions, setRisSessions] = useState<EnhancedChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Load sessions on mount and when refreshTrigger changes
  useEffect(() => {
    if (!ohneSuchliste) loadAllSessions();
  }, [ohneSuchliste]);

  useEffect(() => {
    if (!ohneSuchliste && refreshTrigger && refreshTrigger > 0) {
      loadAllSessions();
    }
  }, [refreshTrigger]);

  // Listen for session events to refresh sidebar
  useEffect(() => {
    if (ohneSuchliste) return;
    const handleSessionCreated = () => {
      console.log('[AppSidebar] Session created, refreshing...');
      loadAllSessions();
    };

    const handleTitleUpdated = () => {
      console.log('[AppSidebar] Session title updated, refreshing...');
      loadAllSessions();
    };

    window.addEventListener('session:created', handleSessionCreated);
    window.addEventListener('session:titleUpdated', handleTitleUpdated);

    return () => {
      window.removeEventListener('session:created', handleSessionCreated);
      window.removeEventListener('session:titleUpdated', handleTitleUpdated);
    };
  }, [ohneSuchliste]);

  // Endet ein Lauf, dem dieser Tab folgt, lädt die Liste still neu: Spinner weg, Punkt „neu“ für das Ergebnis. Ein wegen
  // einer Sperre abgewiesener Start ist kein Laufende (F13-6): die Liste bleibt, wie sie ist, und kein Abruf folgt.
  const previousEndedRunCount = useRef(endedRunCount);
  useEffect(() => {
    const previous = previousEndedRunCount.current;
    previousEndedRunCount.current = endedRunCount;
    if (!ohneSuchliste && endedRunCount > previous) loadAllSessions({ quiet: true });
  }, [endedRunCount]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingSessionId]);

  const loadAllSessions = async ({ quiet = false }: { quiet?: boolean } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);

      const risData = await webChatHistoryService.getSessionsByServiceType('ris', 50, 0);
      setRisSessions(risData);
      // Laufende Recherchen aus der Chatliste (nach einem Reload, von einem anderen Gerät) verfolgt der Tab wieder.
      resumeActiveRuns(risData);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Ihre Recherchen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  // DR-13 UI-11: Gruppen nach Kalendertagen der Ortszeit (utils/datum.ts), auch über die Zeitumstellung.
  const groupSessionsByDate = (sessions: EnhancedChatSession[]): GroupedSessions => {
    const now = new Date();
    const groups: GroupedSessions = { heute: [], gestern: [], woche: [], aelter: [] };
    sessions.forEach(session => groups[zeitgruppe(session.lastMessageAt, now)].push(session));
    return groups;
  };

  const filterSessions = (sessions: EnhancedChatSession[]) => {
    if (!searchQuery.trim()) return sessions;
    const query = searchQuery.toLowerCase();
    return sessions.filter(session => session.title.toLowerCase().includes(query));
  };

  const handleNewChat = () => {
    onNewChat();
  };

  const handleSessionSelect = (session: EnhancedChatSession) => {
    onSessionSelect(session.sessionId);
  };

  const startEditing = (session: EnhancedChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.sessionId);
    setEditingTitle(session.title);
  };

  const saveEdit = async () => {
    if (!editingSessionId || !editingTitle.trim()) return;

    try {
      await webChatHistoryService.updateSessionTitle(editingSessionId, editingTitle.trim());

      // Update local state
      setRisSessions(prev =>
        prev.map(s => (s.sessionId === editingSessionId ? { ...s, title: editingTitle.trim() } : s))
      );

      setEditingSessionId(null);
      setEditingTitle('');
    } catch (err) {
      console.error('Error updating session title:', err);
      setError('Fehler beim Speichern');
    }
  };

  const cancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLoeschFrage(sessionId);
  };

  const loeschen = async (sessionId: string) => {
    setLoeschFrage(null);
    // N-01: der Recherchekontext meldet den Lauf, den das Löschen abbricht, nicht mehr als gesehen.
    window.dispatchEvent(new CustomEvent('session:deleting', { detail: { sessionId, active: true } }));
    try {
      await webChatHistoryService.deactivateSession(sessionId);
      setRisSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      // D4: AppLayout und der Recherchekontext lassen einen gelöschten geöffneten Chat los (leerer Start statt Altstand).
      window.dispatchEvent(new CustomEvent('session:deleted', { detail: { sessionId } }));
    } catch (err) {
      console.error('Error deleting session:', err);
      window.dispatchEvent(new CustomEvent('session:deleting', { detail: { sessionId, active: false } }));
      setError('Fehler beim Löschen');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    else if (e.key === 'Escape') cancelEdit();
  };

  const renderSessionItem = (session: EnhancedChatSession) => {
    const isActive = session.sessionId === currentSessionId;
    const isEditing = editingSessionId === session.sessionId;

    return (
      <div
        key={session.sessionId}
        className={`group relative mx-2 my-1 rounded-xl transition-colors ${
          isActive
            ? 'bg-white dark:bg-gray-700 border-l-[3px] border-l-accent border border-[rgb(var(--cl-ink-rgb)/0.1)] shadow-soft'
            : 'hover:bg-white dark:hover:bg-gray-700/50 border border-transparent'
        }`}
      >
        {isEditing ? (
          <div className="p-2 flex items-center gap-1">
            <input
              ref={editInputRef}
              type="text"
              value={editingTitle}
              onChange={e => setEditingTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-sm px-2 py-1 border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-primary dark:text-white focus:ring-2 focus:ring-accent"
              onClick={e => e.stopPropagation()}
            />
            <button
              onClick={e => { e.stopPropagation(); saveEdit(); }}
              className="p-1 text-accent hover:bg-warm-100 rounded-lg"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); cancelEdit(); }}
              className="p-1 text-muted hover:bg-warm-100 rounded-lg"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleSessionSelect(session)}
            className="w-full text-left p-2"
          >
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <MagnifyingGlassIcon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                {/* Schlusslauf (pruefung-slb N10, Regel 18): ist der Knopf „…“ sichtbar, endet der Titel vor ihm (F10-27: ein
                    Knopf, unter 768 px und auf Touchgeräten 44 px, sonst 32 px, dazu 4 px Rand rechts). */}
                <div className="text-sm font-medium text-primary dark:text-white truncate pr-12 md:pr-0 md:group-hover:pr-10 md:group-focus-within:pr-10 [@media(pointer:coarse)]:!pr-12">
                  {session.title}
                </div>
                <SidebarRunIndicator
                  state={sidebarRunState(session, { searching: isSessionSearching(session.sessionId) || isDetailRunning(session.sessionId), current: isActive, seenRunIds })}
                  time={listenZeit(session.lastMessageAt)}
                />
              </div>
            </div>
          </button>
        )}

        {/* Actions (F10-27): ein Knopf „…“ mit Menü; die Einträge rufen dieselben Funktionen wie die früheren Symbolknöpfe. */}
        {!isEditing && (
          <VerlaufMenue
            titel={session.title}
            teilen={!!teilenIn}
            onUmbenennen={e => startEditing(session, e)}
            onTeilen={() => setTeilen(session)}
            onLoeschen={e => deleteSession(session.sessionId, e)}
            halten={teilen?.sessionId === session.sessionId || loeschFrage === session.sessionId}
          />
        )}
      </div>
    );
  };

  const renderDateGroup = (label: string, sessions: EnhancedChatSession[]) => {
    if (sessions.length === 0) return null;
    return (
      <div key={label}>
        <div className="px-3 py-1 text-sm text-muted font-medium">{label}</div>
        {sessions.map(session => renderSessionItem(session))}
      </div>
    );
  };

  const renderSessionsList = () => {
    const filteredSessions = filterSessions(risSessions);
    const grouped = groupSessionsByDate(filteredSessions);

    if (filteredSessions.length === 0) {
      // K-4 (Korrekturlauf 09.10.2026): ohne Recherchezugang wird die Suchliste nicht geladen; dann sagt die Leiste das,
      // statt „Noch keine Recherchen“ (pausiert, ohne Platz, abgelaufen, Zahlungsverzug, Betreiberkonto).
      return (
        <div className="px-3 py-4 text-sm text-muted text-center">
          {searchQuery ? 'Keine passenden Titel in den geladenen Recherchen.' : zugangOhneSuchliste ? 'Ohne Recherchezugang sind Ihre Recherchen nicht abrufbar.' : 'Noch keine Recherchen'}
        </div>
      );
    }

    return (
      <>
        {renderDateGroup('Heute', grouped.heute)}
        {renderDateGroup('Gestern', grouped.gestern)}
        {renderDateGroup('Letzte 7 Tage', grouped.woche)}
        {renderDateGroup('Älter', grouped.aelter)}
      </>
    );
  };

  return (
    <div
      className={`editorial-sidebar ${
        isCollapsed ? 'w-14' : 'w-72'
      } transition-all duration-200 border-r border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700 glass dark:bg-gray-800 flex flex-col h-full relative z-20`}
    >
      {/* Collapsed Icons */}
      <div
        style={{ visibility: isCollapsed ? 'visible' : 'hidden' }}
        className={`absolute inset-0 flex flex-col items-center pt-1.5 pb-3 transition-opacity duration-200 ${
          isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center flex-1 min-h-0 overflow-y-auto w-full py-1.5 scroll-py-1.5">
          <SidebarBrand collapsed />
          {/* Profil-Nachbau F-10 (PF-607): die Glocke unter der Marke, eingeklappt und am Handy; nie in einer Sicht. Es gibt
              immer nur die Glocke der sichtbaren Form, damit nur ein Zeitgeber den Zähler holt. */}
          {isCollapsed && !gesperrt && <div className="mt-2"><Glocke /></div>}
          <button
            ref={expandButtonRef}
            onClick={onToggleCollapse}
            className="mt-2 h-11 w-11 flex items-center justify-center hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Seitenleiste erweitern"
            aria-label="Seitenleiste erweitern"
          >
            <PanelLeft className="w-4 h-4 text-muted" />
          </button>
          <button
            onClick={handleNewChat}
            disabled={sperre.locked}
            aria-disabled={sperre.locked}
            className="h-11 w-11 flex items-center justify-center hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors mt-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title={sperre.reason ?? 'Neue Recherche'}
            aria-label="Neue Recherche"
          >
            <PlusIcon className="w-4 h-4 text-muted" />
          </button>
          <button
            onClick={() => navigate('/vorlagen')}
            aria-current={aufVorlagen ? 'page' : undefined}
            className={`h-11 w-11 flex items-center justify-center hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors mt-1 ${aufVorlagen ? 'bg-warm-100 dark:bg-gray-700' : ''}`}
            title={VORLAGEN_TEXTE.titel}
            aria-label={VORLAGEN_TEXTE.titel}
          >
            <BookMarked className="w-4 h-4 text-muted" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-shrink-0 mb-2">
          <UserMenu isCollapsed={true} />
        </div>
      </div>

      {/* Expanded Content. F-23 (Fehlersuche Nr. 3, Nutzerblick Mitglied H-11): eingeklappt aus dem Layout (hidden), nicht nur
          unsichtbar; sonst wuchs der Fuß mit dem Kontomenü in der 56 px schmalen Leiste auf 685 px und verlängerte das Dokument
          (bei 390 px vorgescrollte Seite, zwei Scrollleisten). Kein overflow-hidden an der Leiste: es schnitte das Kontomenü ab. */}
      <div
        style={{ visibility: isCollapsed ? 'hidden' : 'visible' }}
        className={`${isCollapsed ? 'hidden' : 'flex'} flex-col h-full min-h-0 transition-opacity duration-200 ${
          isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700 flex items-center justify-between">
            <SidebarBrand />
            {/* F-10 (PF-607): Glocke und Einklappen rechts als Gruppe; die Glocke steht vor „Seitenleiste einklappen“. */}
            <div className="flex items-center">
              {!isCollapsed && !gesperrt && <Glocke />}
              <button
                onClick={onToggleCollapse}
                className="h-11 w-11 flex items-center justify-center hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Seitenleiste einklappen"
                aria-label="Seitenleiste einklappen"
              >
                <PanelLeft className="w-4 h-4 text-muted" />
              </button>
            </div>
          </div>

          {/* New Chat Button */}
          <div className="p-2 border-b border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700">
            {/* F-23 (erster Eindruck, Nutzerblick Mitglied H-08): „Neue Recherche“ als Eintrag wie „Vorlagen“, nicht als zweiter blauer
                Hauptknopf neben „Recherche starten“; ein Wort für die Recherche. */}
            <button
              onClick={handleNewChat}
              disabled={sperre.locked}
              aria-disabled={sperre.locked}
              title={sperre.reason ?? undefined}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <PlusIcon className="w-4 h-4 flex-shrink-0" />
              <span>Neue Recherche</span>
            </button>
            {sperrHinweis && (
              <p role="note" aria-label="Neue Recherche gesperrt" className="mt-2 px-1 text-sm text-muted">
                <span>{sperre.reason}</span>{' '}
                <EinstLink to={zugangZiel} className="font-medium text-primary dark:text-white underline underline-offset-2">{tarifwahl ? 'Tarif wählen' : seitenTitel('zugang')}</EinstLink>
              </p>
            )}
            {teilenIn && (
              <button
                onClick={() => setGeteiltOffen(true)}
                className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                <UserGroupIcon className="w-4 h-4 flex-shrink-0" />
                <span>Geteilte Momentaufnahmen</span>
              </button>
            )}
            <button
              onClick={() => navigate('/vorlagen')}
              aria-current={aufVorlagen ? 'page' : undefined}
              className={`mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 rounded-xl transition-colors ${aufVorlagen ? 'bg-warm-100 dark:bg-gray-700 font-medium' : ''}`}
            >
              <BookMarked className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>{VORLAGEN_TEXTE.titel}</span>
            </button>
          </div>

          {/* Search */}
          <div className="p-2 border-b border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted" />
              <input
                type="text"
                placeholder="In Titeln suchen"
                aria-label="In Titeln der geladenen Recherchen suchen"
                aria-describedby="history-search-help"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 text-primary dark:text-white placeholder-muted rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all"
              />
            </div>
            <p id="history-search-help" className="mt-2 px-1 text-sm text-muted">
              Durchsucht die Titel der geladenen Recherchen. Nachrichteninhalte werden nicht durchsucht.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs mt-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                Schließen
              </button>
            </div>
          )}

          {/* Auch in kurzen Fenstern bleiben mehrere Verläufe über den äußeren Bildlauf erreichbar. */}
          <div className="flex-1 min-h-[8rem] overflow-y-auto">
            {gesperrt ? (
              <p role="status" className="px-3 py-4 text-sm text-muted text-center">{INHALTE_GESPERRT}</p>
            ) : loading && !ohneSuchliste ? (
              <div className="p-4 text-center text-muted">
                <ArrowPathIcon className="w-5 h-5 animate-spin mx-auto mb-2 text-accent" />
                <span className="text-xs">Lade...</span>
              </div>
            ) : (
              <div className="py-2">
                {renderSessionsList()}
              </div>
            )}
          </div>
        </div>

        {/* Footer with User Menu */}
        <div className="flex-shrink-0 border-t border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700">
          <UserMenu isCollapsed={false} />
        </div>
      </div>

      {geteiltOffen && teilenIn && (
        <SharedResearchPanel
          organizationName={teilenIn}
          sperrgrund={sperre.locked ? sperre.reason : null}
          onClose={() => setGeteiltOffen(false)}
          onContinue={sessionId => {
            setGeteiltOffen(false);
            loadAllSessions();
            onSessionSelect(sessionId);
          }}
        />
      )}
      {teilen && teilenIn && (
        <MomentaufnahmeTeilenDialog
          sessionId={teilen.sessionId}
          sessionTitle={teilen.title}
          organizationName={teilenIn}
          onClose={() => setTeilen(null)}
        />
      )}
      {/* F-SL6-1 (F-27): ohne Portal; der Dialog der Anwendung liegt als <dialog> in der obersten Ebene und nimmt den zoom der Hülle. */}
      {loeschFrage && (
        <Dialog title="Diese Recherche löschen?" onClose={() => setLoeschFrage(null)}>
          <p>Möchten Sie diese Recherche wirklich löschen?</p>
          <footer>
            <button type="button" className="adm-button" onClick={() => setLoeschFrage(null)}>Abbrechen</button>
            <button type="button" className="adm-button adm-danger" onClick={() => void loeschen(loeschFrage)}>Löschen</button>
          </footer>
        </Dialog>
      )}
    </div>
  );
};

export default AppSidebar;
